import React, { useState, useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { useBlocker } from "react-router-dom";
import { ActionIcon, Button, Loader, TextInput } from "@mantine/core";
import {
	IconAlertCircle,
	IconCircleCheck,
	IconClock,
	IconLink,
	IconLock,
	IconPencil,
	IconTrashOff,
	IconTrashX,
} from "@tabler/icons-react";
import RichTextEditor from "./RichTextEditor";
import NoteCompareModal from "./NoteCompareModal";
import { BreadcrumbsWithMatterName } from "./Breadcrumbs";

import {
	getNote,
	fetchNoteForComparisonOnConflict,
	updateNote,
	createNote,
	unmountNoteEditor,
	copyNoteLinkToClipboard,
	setNoteLifecycleStatus,
} from "../actions/documentActions";
import { openUnsavedChangesDialog } from "../actions/globalEditDialogActions";
import { setSubheaderText } from "../actions/navigateActions";
import lxwConstants from "../config/lexworkplace-constants";
import { TrashOrArchiveBinMessageForDocumentOrNote } from "./Utils/UnderSubHeaderWarningMessages";
import {
	getDisabledStateFromLifecycleStatuses,
	NORMAL_LIFECYCLE_STATUS,
	TRASH_LIFECYCLE_STATUS,
} from "../utilities/lifecycleStatus";

function NavigationBlocker({ hasUnsavedChanges }) {
	const dispatch = useDispatch();
	// never block the switch to the edit route after creating a note, since
	// the same editor stays mounted (see NoteEditorRoute)
	const blocker = useBlocker(
		({ nextLocation }) =>
			hasUnsavedChanges && !nextLocation.state?.noteEditorKey
	);
	const proceededRef = useRef(false);

	useEffect(() => {
		if (blocker.state === "blocked") {
			proceededRef.current = false;
			dispatch(
				openUnsavedChangesDialog(
					() => {
						proceededRef.current = true;
						blocker.proceed();
					},
					() => {
						if (!proceededRef.current) {
							blocker.reset();
						}
					}
				)
			);
		}
	}, [blocker, dispatch]);

	return null;
}

// a little under the backend's limit, since it checks the size after
// sanitizing, which can grow the HTML slightly
const MAX_NOTE_SIZE = Math.floor(
	lxwConstants.fieldLimits.noteContentBytes * 0.98
);
const AUTOSAVE_DELAY_MS = 1000;
const DEFAULT_NOTE_NAME = "Untitled Note";
// same red Mantine uses for the title input's error state
const ERROR_COLOR = "var(--mantine-color-error)";
// side panel actions have black icons with hyperlink colored text
const ACTION_LINK_STYLES = { label: { color: "var(--lxw-hyperlink)" } };
const NOTE_STATUS_COLORS = {
	error: {
		color: ERROR_COLOR,
		background: "var(--mantine-color-red-light)",
	},
	success: {
		color: "var(--mantine-color-green-light-color)",
		background: "var(--mantine-color-green-light)",
	},
	busy: {
		color: "var(--mantine-color-blue-light-color)",
		background: "var(--mantine-color-blue-light)",
	},
	neutral: {
		color: "var(--mantine-color-gray-light-color)",
		background: "var(--mantine-color-gray-light)",
	},
};

// Rendered as the page subheader title (see setSubheaderText). Shows the name
// with a pencil that switches it to a text box, and switches back once the
// text box blurs with a non-empty name.
function NoteTitle({ name, startEditing, disabled, onChange, onBlur }) {
	const [editing, setEditing] = useState(startEditing);
	const [touched, setTouched] = useState(false);

	if (!editing || disabled) {
		// the name truncates itself so a long one doesn't push the pencil out
		// of the subheader, which clips its overflow
		return (
			<div style={{ display: "flex", alignItems: "center" }}>
				<span
					title={name}
					style={{
						minWidth: 0,
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{name}
				</span>
				{disabled ? null : (
					<ActionIcon
						variant="subtle"
						color="gray"
						title="Rename Note"
						aria-label="Rename Note"
						onClick={() => setEditing(true)}
						style={{ marginLeft: 6, marginRight: 6, flexShrink: 0 }}
					>
						<IconPencil size={18} />
					</ActionIcon>
				)}
			</div>
		);
	}

	// wait until the field is touched so a new note doesn't open in an error state
	const showError = touched && !name.trim();

	// the error message sits beside the input since the subheader is a single line
	return (
		<>
			<TextInput
				autoFocus
				size="md"
				placeholder="Note name"
				aria-label="Note name"
				value={name}
				error={showError}
				// select the whole name so typing replaces it (e.g. the default name)
				onFocus={(event) => event.currentTarget.select()}
				onChange={(event) => {
					setTouched(true);
					onChange(event.currentTarget.value);
				}}
				onBlur={() => {
					setTouched(true);
					if (name.trim()) {
						setEditing(false);
					}
					onBlur();
				}}
				onKeyDown={(event) => {
					if (event.key === "Enter") {
						event.currentTarget.blur();
					}
				}}
				style={{ display: "inline-block", verticalAlign: "middle" }}
				styles={{ input: { width: "min(400px, 60vw)", fontSize: 16 } }}
			/>
			{showError ? (
				<span
					style={{
						marginLeft: 8,
						fontSize: 14,
						verticalAlign: "middle",
						color: "var(--mantine-color-error)",
					}}
				>
					Note name is required
				</span>
			) : null}
		</>
	);
}

// display note times from the UNIX_TIMESTAMP() values rather than modifiedOn,
// which the mysql driver can shift when the db and server timezones differ
function unixToDate(seconds) {
	return new Date(seconds * 1000);
}

function plainTextToHtml(text) {
	const escaped = text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
	return escaped
		.split(/\n\n+/)
		.map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
		.join("");
}

function noteContentToHtml(note) {
	return (note.noteContentType || "plain") === "plain"
		? plainTextToHtml(note.content || "")
		: note.content || "";
}

function NoteEditor(props) {
	const [name, setName] = useState(props.createMode ? DEFAULT_NOTE_NAME : "");
	const [content, setContent] = useState("");
	const noteContentType = "html";
	const [contentReady, setContentReady] = useState(props.createMode);
	const [saveStatus, setSaveStatus] = useState(null);
	// { modifiedOnUnix, modifiedBy } shown in the side panel, kept current after saves
	const [lastModified, setLastModified] = useState(null);
	// set to the server's { modifiedOnUnix, modifiedOn, modifiedBy } when
	// someone else saved the note first; autosave stops until it is resolved
	const [conflict, setConflict] = useState(null);
	const [compareOpen, setCompareOpen] = useState(false);
	// { html } of the latest saved version for the compare modal, or { error }
	const [latestVersion, setLatestVersion] = useState(null);
	// the note's modifiedOn as of our last load/save, so the server can
	// reject our save if someone else has saved since
	const expectedModifiedOnUnix = useRef(null);
	// null until a new note is created, so save() knows to create it
	const noteIDRef = useRef(
		props.createMode ? null : props.match.params.fullpath
	);
	// a new note has nothing to load, and loading it after it is created
	// mustn't replace edits made while the create request was in flight
	const initialized = useRef(props.createMode);
	// name and content as last loaded or saved, to detect unsaved changes.
	// A new note starts out matching its default name so it isn't created
	// until the user renames it or types something.
	const savedNote = useRef({
		name: props.createMode ? DEFAULT_NOTE_NAME : "",
		content: "",
	});
	// latest render's values, so save() can be called from stale closures
	const latestNote = useRef({});
	const saving = useRef(false);
	const resaveWhenDone = useRef(false);
	const createStarted = useRef(false);
	const mounted = useRef(true);

	useEffect(() => {
		if (!props.createMode) {
			props.getNote(props.match.params.fullpath);
		}
		return () => {
			mounted.current = false;
			// flush any change still waiting on the autosave delay
			save();
			props.unmountNoteEditor();
		};
	}, []);

	useEffect(() => {
		if (
			!initialized.current &&
			!props.loading &&
			!props.createMode &&
			props.note
		) {
			initialized.current = true;
			setName(props.note.name);
			const initialContent = noteContentToHtml(props.note);
			setContent(initialContent);
			savedNote.current = {
				name: props.note.name,
				content: initialContent,
			};
			expectedModifiedOnUnix.current = props.note.modifiedOnUnix;
			setLastModified({
				modifiedOnUnix: props.note.modifiedOnUnix,
				modifiedBy: props.note.modifiedBy,
			});
			setConflict(null);
			setSaveStatus(null);
			setContentReady(true);
		}
	}, [props.loading, props.note, props.createMode]);

	// replaces our unsaved changes with the latest saved version of the note,
	// which is the only way out of a conflict (stale changes can't be saved)
	function loadLatestVersion() {
		setCompareOpen(false);
		initialized.current = false;
		setContentReady(false);
		props.getNote(props.match.params.fullpath);
	}

	function openCompare() {
		setCompareOpen(true);
		setLatestVersion(null);
		props
			.fetchNoteForComparisonOnConflict(props.match.params.fullpath)
			.then((note) => setLatestVersion({ html: noteContentToHtml(note) }))
			.catch(() => setLatestVersion({ error: true }));
	}

	const noteLifecycleStatusObject = getDisabledStateFromLifecycleStatuses({
		matterLifecycleStatus: props.note?.matterLifecycleStatus,
		folderLifecycleStatus: props.note?.folderLifecycleStatus,
		matterNoteLifecycleStatus: props.note?.matterNoteLifecycleStatus,
	});
	const noteEditingDisabled = noteLifecycleStatusObject.disabled;
	const noteID = props.match.params.fullpath;
	const noteInTrash =
		props.note?.matterNoteLifecycleStatus === TRASH_LIFECYCLE_STATUS;
	// the note can't be moved in or out of the trash while its matter or folder is there
	const noteTrashActionAvailable = !getDisabledStateFromLifecycleStatuses({
		matterLifecycleStatus: props.note?.matterLifecycleStatus,
		folderLifecycleStatus: props.note?.folderLifecycleStatus,
	}).disabled;

	const noteNameEmpty = name.trim().length === 0;
	const contentBytes = new Blob([content]).size;
	const overLimit = contentBytes > MAX_NOTE_SIZE;
	const canSave = !noteNameEmpty && !overLimit && !noteEditingDisabled;
	const hasUnsavedChanges =
		name !== savedNote.current.name ||
		content !== savedNote.current.content;
	latestNote.current = { name, content, canSave, conflict };

	// Creates the note if it doesn't exist yet (then switches to the edit
	// route), otherwise updates it. Only reads refs and props that don't change
	// before the note is created, so it is safe to call from the unmount
	// cleanup and the subheader title's callbacks.
	function save() {
		const { name, content, canSave, conflict } = latestNote.current;
		const unchanged =
			name === savedNote.current.name &&
			content === savedNote.current.content;
		if (!canSave || unchanged || conflict || createStarted.current) {
			return;
		}
		if (saving.current) {
			resaveWhenDone.current = true;
			return;
		}
		saving.current = true;
		const creating = noteIDRef.current === null;
		createStarted.current = creating;
		setSaveStatus("saving");
		const noteData = { name, content, noteContentType };
		const request = creating
			? props.createNote(
					props.match.params.matterID,
					props.match.params.directoryID,
					noteData
				)
			: props.updateNote(noteIDRef.current, {
					...noteData,
					expectedModifiedOnUnix: expectedModifiedOnUnix.current,
				});
		request
			.then((result) => {
				savedNote.current = { name, content };
				if (!creating) {
					setSaveStatus("saved");
					expectedModifiedOnUnix.current = result.modifiedOnUnix;
					setLastModified(result);
					return;
				}
				if (!mounted.current) {
					return;
				}
				// load the new note before switching to the edit route, so the
				// side panel has its details and later saves have its modifiedOn
				const newNoteID = String(result.matterNoteID);
				return props.getNote(newNoteID).then((note) => {
					if (!note || !mounted.current) {
						return;
					}
					noteIDRef.current = newNoteID;
					expectedModifiedOnUnix.current = note.modifiedOnUnix;
					setLastModified({
						modifiedOnUnix: note.modifiedOnUnix,
						modifiedBy: note.modifiedBy,
					});
					setSaveStatus("saved");
					createStarted.current = false;
					// save anything typed while the note was being created
					resaveWhenDone.current = true;
					// keep this editor mounted, see NoteEditorRoute
					props.history.replace(`/notes/${newNoteID}`, {
						state: { noteEditorKey: props.editorKey },
					});
				});
			})
			.catch((err) => {
				createStarted.current = false;
				if (
					err?.lxwErrorCode ===
					lxwConstants.errorCodes.NOTE_EDIT_CONFLICT.code
				) {
					setConflict(err.lxwErrorData);
					setSaveStatus(null);
				} else {
					setSaveStatus("error");
				}
			})
			.finally(() => {
				saving.current = false;
				if (resaveWhenDone.current) {
					resaveWhenDone.current = false;
					save();
				}
			});
	}

	// autosave edits once typing stops, which also creates new notes
	useEffect(() => {
		if (!hasUnsavedChanges || !canSave || conflict) {
			return;
		}
		const timer = setTimeout(save, AUTOSAVE_DELAY_MS);
		return () => clearTimeout(timer);
	}, [name, content]);

	// the create route has no note record yet, so fall back to the route
	// params and the matter page's details
	const breadcrumbProps = props.createMode
		? {
				matterID: props.match.params.matterID,
				matterName:
					String(props.matterDetails?.id) ===
					props.match.params.matterID
						? props.matterDetails.name
						: "Matter",
				path: `/${props.match.params.fullpath || ""}`.replace(
					/\/$/,
					""
				),
			}
		: {
				matterID: props.note?.matterID,
				matterName: props.note?.matterName,
				path: props.note?.path || "",
				directoryHierarchy: props.note?.directoryHierarchy,
				matterLifecycleStatus: props.note?.matterLifecycleStatus,
			};

	useEffect(() => {
		props.setSubheaderText({
			subheaderText: contentReady ? (
				<div style={{ marginLeft: 12 }}>
					<div className="subHeaderBreadcrumbs Breadcrumbs">
						<BreadcrumbsWithMatterName
							{...breadcrumbProps}
							name=""
							rootPath={`/${rootSegment}/`}
							rootName={"Notes"}
							showMatterIconWithMatterName
						/>
					</div>
					<NoteTitle
						name={name}
						startEditing={props.createMode}
						disabled={noteEditingDisabled || !!conflict}
						onChange={setName}
						onBlur={save}
					/>
				</div>
			) : (
				""
			),
			subHeaderHeight: 90,
		});
	}, [name, contentReady, noteEditingDisabled, conflict, breadcrumbProps]);

	useEffect(() => {
		const handler = (e) => {
			if (hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [hasUnsavedChanges]);

	if (props.loading && !props.createMode) {
		return <div>Loading...</div>;
	}

	let noteStatus;
	if (noteEditingDisabled) {
		noteStatus = { text: "Read only", tone: "neutral", Icon: IconLock };
	} else if (conflict) {
		noteStatus = {
			text: `${conflict.modifiedBy || "Someone else"} saved a newer version of this note at ${unixToDate(conflict.modifiedOnUnix).toLocaleTimeString()}, so your recent changes can't be saved. Compare the versions and copy anything you want to keep, then load the latest version.`,
			tone: "error",
			Icon: IconAlertCircle,
		};
	} else if (saveStatus === "saving") {
		noteStatus = { text: "Saving...", tone: "busy", Icon: null };
	} else if (hasUnsavedChanges && saveStatus === "error") {
		noteStatus = {
			text: "Unable to save changes",
			tone: "error",
			Icon: IconAlertCircle,
		};
	} else if (hasUnsavedChanges && noteNameEmpty) {
		noteStatus = {
			text: "A note name is required to save",
			tone: "error",
			Icon: IconAlertCircle,
		};
	} else if (hasUnsavedChanges && overLimit) {
		noteStatus = {
			text: "Note is too long to save",
			tone: "error",
			Icon: IconAlertCircle,
		};
	} else if (hasUnsavedChanges) {
		noteStatus = { text: "Changes pending", tone: "busy", Icon: IconClock };
	} else if (saveStatus === "saved") {
		noteStatus = {
			text: "All changes saved",
			tone: "success",
			Icon: IconCircleCheck,
		};
	} else {
		noteStatus = {
			text: "No changes made",
			tone: "neutral",
			Icon: IconCircleCheck,
		};
	}
	const noteStatusColors = NOTE_STATUS_COLORS[noteStatus.tone];

	const rootSegment = breadcrumbProps.path.split("/")[1];

	const sizePercent = Math.min((contentBytes / MAX_NOTE_SIZE) * 100, 100);
	const sizeLabel = `${(Math.floor((contentBytes / MAX_NOTE_SIZE) * 1000) / 10).toFixed(1)}%`;

	return (
		<>
			{/* savable changes are flushed on unmount, so only block for ones that can't be saved */}
			<NavigationBlocker
				hasUnsavedChanges={hasUnsavedChanges && (!canSave || conflict)}
			/>
			{noteEditingDisabled
				? TrashOrArchiveBinMessageForDocumentOrNote({
						...noteLifecycleStatusObject,
						objectType: "Matter Note",
					})
				: null}

			<div style={{ height: "100%", margin: 0 }}>
				{contentReady && (
					<RichTextEditor
						content={content}
						disabled={noteEditingDisabled}
						// stale changes can't be saved, but can still be copied
						readOnly={!!conflict}
						readOnlyMessage="Read only: this note has a newer saved version. You can still select and copy your text."
						onChange={setContent}
						onInit={(html) => {
							setContent(html);
							savedNote.current = {
								...savedNote.current,
								content: html,
							};
						}}
						sidePanel={
							<div
								style={{
									width: 400,
									flexShrink: 0,
									boxSizing: "border-box",
									padding: 24,
									borderLeft: "1px solid #e0e0e0",
									overflowY: "auto",
									display: "flex",
									flexDirection: "column",
									gap: 24,
								}}
							>
								{!props.createMode && props.note && (
									<div
										style={{
											background: "#f0f0f0",
											padding: "16px 20px",
											lineHeight: 1.8,
										}}
									>
										<div>
											Created{" "}
											{new Date(
												props.note.createdOn
											).toLocaleDateString()}{" "}
											by {props.note.createdBy}
										</div>
										{lastModified && (
											<div>
												Last Modified{" "}
												{unixToDate(
													lastModified.modifiedOnUnix
												).toLocaleDateString()}{" "}
												by {lastModified.modifiedBy}
											</div>
										)}
									</div>
								)}
								<div>
									<div
										role="status"
										style={{
											display: "flex",
											alignItems: "center",
											gap: 8,
											padding: "10px 12px",
											marginBottom: 16,
											borderRadius: 4,
											fontSize: 15,
											fontWeight: 500,
											...noteStatusColors,
										}}
									>
										<div
											style={{
												flexShrink: 0,
											}}
										>
											{noteStatus.Icon ? (
												<noteStatus.Icon
													style={{
														verticalAlign: "sub",
													}}
													size={20}
												/>
											) : (
												<Loader
													style={{
														verticalAlign: "sub",
													}}
													size={18}
													color="currentColor"
												/>
											)}
										</div>
										<div>
											{noteStatus.text}
											{conflict ? (
												<div
													style={{
														display: "flex",
														flexWrap: "wrap",
														gap: 8,
														marginTop: 10,
													}}
												>
													<Button
														size="xs"
														variant="default"
														onClick={openCompare}
													>
														Compare
													</Button>
													<Button
														size="xs"
														color="red"
														onClick={
															loadLatestVersion
														}
													>
														Load latest version
													</Button>
												</div>
											) : null}
										</div>
									</div>
									<div
										style={{
											height: 6,
											borderRadius: 3,
											background: "#e0e0e0",
											overflow: "hidden",
										}}
									>
										<div
											style={{
												height: "100%",
												width: `${sizePercent}%`,
												background: overLimit
													? ERROR_COLOR
													: "var(--lxw-main-500)",
												transition: "width 0.2s",
											}}
										/>
									</div>
									<div
										style={{
											textAlign: "right",
											fontSize: 12,
											marginTop: 4,
											color: overLimit
												? ERROR_COLOR
												: "#666",
										}}
									>
										{sizeLabel}
									</div>
									{overLimit ? (
										<div
											style={{
												color: ERROR_COLOR,
												fontSize: 12,
												marginTop: 4,
											}}
										>
											This note is too long and cannot be
											saved. Please shorten it to
											continue.
										</div>
									) : null}
								</div>
								{!props.createMode && props.note && (
									<div>
										<div
											style={{
												fontSize: 18,
												paddingBottom: 8,
												marginBottom: 8,
												borderBottom:
													"1px solid #e0e0e0",
											}}
										>
											Actions
										</div>
										<Button
											variant="subtle"
											color="black"
											justify="flex-start"
											fullWidth
											styles={ACTION_LINK_STYLES}
											leftSection={<IconLink size={20} />}
											onClick={() =>
												props.copyNoteLinkToClipboard(
													noteID
												)
											}
										>
											Copy Internal Note URL
										</Button>
										{noteTrashActionAvailable && (
											<Button
												variant="subtle"
												color="black"
												justify="flex-start"
												fullWidth
												// wait for pending edits to save so they aren't lost when the note becomes read only
												disabled={hasUnsavedChanges}
												// let Mantine gray out the label while disabled
												styles={
													hasUnsavedChanges
														? undefined
														: ACTION_LINK_STYLES
												}
												leftSection={
													noteInTrash ? (
														<IconTrashOff
															size={20}
														/>
													) : (
														<IconTrashX size={20} />
													)
												}
												onClick={() => {
													props
														.setNoteLifecycleStatus(
															noteID,
															noteInTrash
																? NORMAL_LIFECYCLE_STATUS
																: TRASH_LIFECYCLE_STATUS
														)
														.then((note) => {
															// moving to/from the trash bumps modifiedOn, which
															// would otherwise look like someone else's save
															if (note) {
																expectedModifiedOnUnix.current =
																	note.modifiedOnUnix;
															}
														});
												}}
											>
												{noteInTrash
													? "Restore Note from Trash Bin"
													: "Move Note to Trash Bin"}
											</Button>
										)}
									</div>
								)}
							</div>
						}
					/>
				)}
			</div>
			<NoteCompareModal
				opened={compareOpen}
				onClose={() => setCompareOpen(false)}
				latestVersion={latestVersion}
				yourHtml={content}
				onLoadLatest={loadLatestVersion}
			/>
		</>
	);
}

const mapStateToProps = (state) => ({
	note: state.note.details,
	loading: state.note.loading,
	matterID: state.note.matterID,
	fullpath: state.note.fullpath,
	directoryID: state.note.directoryID,
	path: state.note.path,
	matterDetails: state.matter.matterDetails,
});
const ConnectedNoteEditor = connect(mapStateToProps, {
	getNote,
	fetchNoteForComparisonOnConflict,
	updateNote,
	createNote,
	unmountNoteEditor,
	setSubheaderText,
	copyNoteLinkToClipboard,
	setNoteLifecycleStatus,
})(NoteEditor);

// The create and edit routes share a RouteElement key, so remounting is
// decided here: a fresh editor for every navigation, except the replace to
// the edit route after a note is created, which carries the create page's key
// forward so anything typed while it was being created isn't lost.
export default function NoteEditorRoute(props) {
	const editorKey =
		props.location?.state?.noteEditorKey ?? props.location?.key;
	return (
		<ConnectedNoteEditor key={editorKey} editorKey={editorKey} {...props} />
	);
}
